import { TIME_SERIES_AGGREGATORS, UNASSIGNED } from '@/lib/constants';
import { UNASSIGNED_VALUE } from '@/composables/grid-page/chart/utils';
import { buildChartPointFilter } from '@/composables/grid-page/chart/chart-point-filter';
import { computed, ref } from 'vue';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { isNullOrUndefined } from 'shared/lib/object/object';

const propKeyFromRef = (ref) => {
  if (ref.isProperty) return ref.property?.uid ?? null;
  return ref.edge ?? null;
};

export default function useChartPointClick({ chart, chartDataQuery, accountUid, idPrefix }) {
  const showGoalList = ref(false);
  const goalListFilter = ref(null);
  const goalListTitle = ref('');
  const goalListCycles = ref([]);
  const hideCycleSelector = ref(false);

  const maxDialogWidth = computed(() => (window.innerWidth > 1400 ? '1400px' : 'calc(100vw - 15rem)'));
  const tempGoalListId = computed(() => `${idPrefix}_${goalListTitle.value}`);

  const isClickable = computed(() => chart.value.source === goalConfig.model
    && !TIME_SERIES_AGGREGATORS.includes(chart.value.aggregationConfig?.aggregation?.aggregator));

  const goalListVisibleProps = computed(() => [
    chart.value.groupBy,
    chart.value.aggregationConfig?.stackOptions,
    chart.value.aggregationConfig?.aggregation,
  ]
    .filter((ref) => !isNullOrUndefined(ref))
    .map(propKeyFromRef)
    .filter((key) => !isNullOrUndefined(key)));

  const handlePointClick = ({ categoryIndex }) => {
    if (!isClickable.value) {
      return;
    }

    const result = chartDataQuery.result.value;
    if (result === null || !result.filteredCategories) {
      return;
    }

    const category = result.filteredCategories[categoryIndex];
    if (!category) {
      return;
    }

    const { groupBy } = chart.value;
    const chartCycles = chart.value.goalCycles || [];
    const isGoalCycleGrouping = groupBy.edge === goalConfig.edges.goalCycle;

    if (isGoalCycleGrouping && category.originalValue === UNASSIGNED_VALUE && chartCycles.length > 0) {
      return;
    }

    goalListTitle.value = String(category.title);
    goalListFilter.value = buildChartPointFilter({
      chart: { ...chart.value, filter: chartDataQuery.enrichedFilter.value },
      accountUid: accountUid.value,
      category,
      chartGoalCycles: isGoalCycleGrouping ? chartCycles : undefined,
    });

    hideCycleSelector.value = isGoalCycleGrouping && chartCycles.length > 0;

    if (isGoalCycleGrouping && category.originalValue === UNASSIGNED_VALUE) {
      goalListCycles.value = [{ uid: UNASSIGNED }];
    } else if (isGoalCycleGrouping && chartCycles.length > 0) {
      goalListCycles.value = [];
    } else {
      goalListCycles.value = chartCycles;
    }

    showGoalList.value = true;
  };

  const handleMetricClick = () => {
    if (chart.value.source !== goalConfig.model) {
      return;
    }
    goalListTitle.value = '';
    goalListFilter.value = chartDataQuery.enrichedFilter.value;
    goalListCycles.value = chart.value.goalCycles || [];
    hideCycleSelector.value = false;
    showGoalList.value = true;
  };

  return {
    showGoalList,
    goalListFilter,
    goalListTitle,
    goalListCycles,
    hideCycleSelector,
    maxDialogWidth,
    tempGoalListId,
    goalListVisibleProps,
    handlePointClick,
    handleMetricClick,
    isClickable,
  };
}
